<template>
  <div class="user-subscription-history">
    <div class="history-header">
      <div class="header-content">
        <div>
          <p class="history-description">
            История всех ваших платежей за абонементы
          </p>
        </div>
        <a-button 
          type="primary" 
          :loading="loading" 
          @click="loadData"
        >
          <template #icon>
            <ReloadOutlined />
          </template>
          Обновить
        </a-button>
      </div>
    </div>

    <div v-if="loading" class="loading-history">
      <a-spin />
      <p>Загрузка данных...</p>
    </div>

    <div v-else-if="paymentHistory.length === 0" class="no-payment-history">
      <p>У вас пока нет зарегистрированных платежей</p>
    </div>

    <div v-else class="history-content">
      <!-- История оплат -->
        <div class="payment-history-list">
          <div 
            v-for="payment in paymentHistory" 
            :key="payment.id" 
            class="payment-history-item"
            :class="{ 'pending': payment.status === 'pending', 'completed': payment.status === 'completed', 'rejected': payment.status === 'rejected' }"
          >
            <div class="payment-header-row">
              <div class="payment-icon">
                <div class="icon-container" :class="payment.status">
                  <DollarOutlined v-if="payment.status === 'completed'" />
                  <ClockCircleOutlined v-else-if="payment.status === 'pending'" />
                  <CloseCircleOutlined v-else-if="payment.status === 'rejected'" />
                </div>
              </div>
              
              <span class="payment-status" :class="payment.status">
                {{ getStatusLabel(payment.status) }}
              </span>
            </div>
            
            <div class="payment-content">
              <div class="team-name">{{ payment.team_name }}</div>
              
              <div class="payment-amount">{{ formatAmount(payment.amount) }} ₽</div>
              
              <div class="payment-details">
                <div class="detail-item">
                  <CalendarOutlined class="detail-icon" />
                  <span>{{ formatMonth(payment.payment_month) }}</span>
                </div>
                <div class="detail-item">
                  <ClockCircleOutlined class="detail-icon" />
                  <span>{{ formatDate(payment.payment_date) }}</span>
                </div>
              </div>
              
              <div v-if="payment.comment" class="payment-comment">
                <CommentOutlined class="comment-icon" />
                <span>{{ payment.comment }}</span>
              </div>
              
              <div v-if="payment.receipt_url && isRealReceiptUrl(payment.receipt_url)" class="receipt-link">
                <a-button 
                  type="primary" 
                  size="small"
                  ghost
                  @click="viewReceipt(payment.receipt_url)"
                  class="receipt-button"
                >
                  <EyeOutlined />
                  Квитанция
                </a-button>
              </div>
            </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import { api } from '../services/api';
import { Spin, Button } from 'ant-design-vue';
import { 
  ReloadOutlined,
  EyeOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  CommentOutlined
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';

export default {
  name: 'UserSubscriptionHistory',
  components: {
    'a-spin': Spin,
    'a-button': Button,
    ReloadOutlined,
    EyeOutlined,
    DollarOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    CalendarOutlined,
    CommentOutlined
  },
  data() {
    return {
      paymentHistory: [],
      loading: false
    };
  },
  async mounted() {
    this.loading = true;
    await this.loadData();
  },
  methods: {
    async loadData() {
      await this.loadPaymentHistory();
    },

    async loadPaymentHistory() {
      try {
        this.paymentHistory = await api.getUserPaymentHistory();
        
        // Оставляем только реальные квитанции (без добавления тестовых URL)
        // Тестовые URL будут показываться только если они уже есть в базе данных
        
        // Если история пуста, показываем сообщение об отсутствии платежей
        // Тестовые данные больше не добавляются автоматически
        
      } catch (error) {
        console.error('Error loading payment history:', error);
        this.$message.error('Ошибка при загрузке истории платежей');
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return dayjs(dateString).format('DD.MM.YYYY');
    },

    formatMonth(monthString) {
      if (!monthString) return '';
      return dayjs(monthString + '-01').format('MMMM YYYY');
    },

    formatAmount(amount) {
      if (!amount) return '0';
      return amount.toLocaleString('ru-RU');
    },

    getStatusLabel(status) {
      const labels = {
        pending: 'На проверке',
        completed: 'Подтвержден',
        rejected: 'Отклонен'
      };
      return labels[status] || status;
    },

    viewReceipt(url) {
      window.open(url, '_blank');
    },

    isRealReceiptUrl(url) {
      // Проверяем, что URL не является тестовым (не содержит example.com)
      return url && !url.includes('example.com') && url.startsWith('http');
    }
  }
};
</script>

<style scoped>
.user-subscription-history {
  padding: 16px;
}

.history-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.header-content h4 {
  margin-bottom: 8px;
  color: #1890ff;
  font-size: 16px;
}

.history-description {
  color: #666;
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-content .ant-btn {
    align-self: flex-start;
  }
}

.loading-history {
  text-align: center;
  padding: 40px;
  color: #666;
}

.no-payment-history {
  text-align: center;
  padding: 40px;
  color: #999;
  font-style: italic;
}

.history-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.payment-history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-history-item {
  padding: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.payment-history-item:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.payment-history-item.pending {
  border-color: #faad14;
  background: linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%);
}

.payment-history-item.completed {
  border-color: #52c41a;
  background: linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%);
}

.payment-history-item.rejected {
  border-color: #ff4d4f;
  background: linear-gradient(135deg, #fff2f0 0%, #fff1f0 100%);
}

.payment-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.payment-icon {
  flex-shrink: 0;
}

.icon-container {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
  transition: all 0.3s ease;
}

.icon-container.completed {
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
  box-shadow: 0 4px 12px rgba(82, 196, 26, 0.3);
}

.icon-container.pending {
  background: linear-gradient(135deg, #faad14 0%, #ffc53d 100%);
  box-shadow: 0 4px 12px rgba(250, 173, 20, 0.3);
}

.icon-container.rejected {
  background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
  box-shadow: 0 4px 12px rgba(255, 77, 79, 0.3);
}

.payment-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.team-name {
  font-weight: 600;
  color: #262626;
  font-size: 18px;
  line-height: 1.4;
}

.payment-amount {
  font-weight: 700;
  color: #1890ff;
  font-size: 20px;
  line-height: 1.2;
}

.payment-status {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
}

.payment-status.pending {
  background: #fff7e6;
  color: #d46b08;
  border: 1px solid #ffd591;
}

.payment-status.completed {
  background: #f6ffed;
  color: #389e0d;
  border: 1px solid #b7eb8f;
}

.payment-status.rejected {
  background: #fff2f0;
  color: #cf1322;
  border: 1px solid #ffccc7;
}

.payment-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #595959;
}

.detail-icon {
  color: #8c8c8c;
  font-size: 14px;
  width: 16px;
}

.payment-comment {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.5;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 8px;
  border-left: 3px solid #d9d9d9;
}

.comment-icon {
  color: #8c8c8c;
  font-size: 14px;
  margin-top: 1px;
  flex-shrink: 0;
}

.receipt-link {
  display: flex;
  align-items: center;
}

.receipt-button {
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.receipt-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
}

@media (max-width: 768px) {
  .payment-history-item {
    padding: 8px;
    gap: 0px;
  }
  
  .icon-container {
    width: 32px;
    height: 32px;
    font-size: 18px;
  }
  
  .payment-header-row {
    gap: 8px;
  }
  
  .team-name {
    font-size: 16px;
  }
  
  .payment-amount {
    font-size: 18px;
  }
}
</style> 